import React from 'react';

interface PaginationProps {
    currentPage: number;
    lastPage: number;
    onPageChange: (page: number) => void;
}

/** Page numbers to show around `current`, with '...' gaps, always including 1 and `last`. */
function buildPageWindow(current: number, last: number): (number | '...')[] {
    const delta = 1;
    const middle: number[] = [];
    for (let i = Math.max(2, current - delta); i <= Math.min(last - 1, current + delta); i++) {
        middle.push(i);
    }

    const window: (number | '...')[] = [1];
    if (middle[0] > 2) window.push('...');
    window.push(...middle);
    if (middle[middle.length - 1] < last - 1) window.push('...');
    if (last > 1) window.push(last);
    return window;
}

export default function Pagination({ currentPage, lastPage, onPageChange }: PaginationProps) {
    const pages = buildPageWindow(currentPage, lastPage);
    const arrowClasses = "px-3 py-2 rounded-lg text-sm font-bold transition-colors duration-300 bg-gray-800 text-gray-200 hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-800";

    return (
        <div className="flex justify-center flex-wrap items-center gap-2 font-mono">
            <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                aria-label="First page"
                className={arrowClasses}
            >
                «
            </button>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className={arrowClasses}
            >
                ‹
            </button>

            {pages.map((p, i) =>
                p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-sm text-gray-500 select-none">
                        …
                    </span>
                ) : (
                    <button
                        key={p}
                        onClick={() => onPageChange(p)}
                        disabled={p === currentPage}
                        className={`px-4 py-2 rounded-lg text-sm transition-colors duration-300 cursor-pointer disabled:cursor-default ${
                            p === currentPage
                                ? 'bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-white font-bold'
                                : 'bg-gray-800 text-gray-200 hover:bg-gray-700/50'
                        }`}
                    >
                        {p}
                    </button>
                )
            )}

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === lastPage}
                aria-label="Next page"
                className={arrowClasses}
            >
                ›
            </button>
            <button
                onClick={() => onPageChange(lastPage)}
                disabled={currentPage === lastPage}
                aria-label="Last page"
                className={arrowClasses}
            >
                »
            </button>
        </div>
    );
}
